import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router'
import { LayoutDashboard, Settings, LogOut, Loader2, X } from 'lucide-react'
import { Logo } from './logo'
import { useAuth, useSignOut, useCurrentUser } from '@/features/auth'
import { cn } from '@/shared/lib/utils'
import { NotificationNavItem } from './notification-nav-item'

interface NavItemProps {
  icon: React.ReactNode
  label: string
  href: string
  isActive?: boolean
}

function NavItem({ icon, label, href, isActive }: NavItemProps) {
  const navigate = useNavigate()

  return (
    <button
      onClick={() => navigate(href)}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-6 py-3 text-left text-sm font-medium transition-colors',
        isActive
          ? 'bg-white/10 text-white'
          : 'text-white/70 hover:bg-white/5 hover:text-white'
      )}
    >
      {icon}
      {label}
    </button>
  )
}

interface SidebarProps {
  isMobile?: boolean
  onClose?: () => void
}

export function Sidebar({ isMobile = false, onClose }: SidebarProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const { user } = useAuth()
  const { user: profile } = useCurrentUser()
  const { signOut, isSigningOut } = useSignOut()

  const isActive = (path: string) => location.pathname.startsWith(path)

  // Get user initials for avatar
  const getUserInitials = () => {
    if (profile?.full_name) {
      const names = profile.full_name.split(' ')
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase()
      }
      return profile.full_name.substring(0, 2).toUpperCase()
    }
    return user?.email?.charAt(0).toUpperCase() || '?'
  }

  // Get display name
  const getDisplayName = () => {
    if (profile?.full_name) {
      const names = profile.full_name.split(' ')
      if (names.length >= 2) {
        return `${names[0]} ${names[1][0]}.`
      }
      return profile.full_name
    }
    return user?.email?.split('@')[0] || ''
  }

  return (
    <aside className="flex h-full w-[260px] flex-col justify-between bg-primary py-6">
      {/* Top section */}
      <div className="flex min-h-0 flex-1 flex-col gap-8">
        {/* Logo + Mobile close */}
        <div className="flex items-center justify-between px-6">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white">
              <Logo className="h-5 w-5 text-primary" />
            </div>
            <span className="text-xl font-bold text-white">HeyNicolai</span>
          </div>
          {isMobile && onClose && (
            <button
              onClick={onClose}
              className="rounded p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex min-h-0 flex-1 flex-col gap-1 px-3">
          {/* Notifications - positioned above Dashboard */}
          <NotificationNavItem />

          <NavItem
            icon={<LayoutDashboard className="h-5 w-5" />}
            label={t('nav.dashboard')}
            href="/dashboard"
            isActive={location.pathname === '/dashboard'}
          />
        </nav>
      </div>

      {/* Bottom section */}
      <div className="flex flex-col gap-1 px-3">
        {/* Settings */}
        <NavItem
          icon={<Settings className="h-5 w-5" />}
          label={t('nav.settings')}
          href="/settings"
          isActive={isActive('/settings')}
        />

        {/* User profile */}
        <div className="mt-2 rounded-lg bg-white/5 px-3 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-sm font-semibold text-white">
              {getUserInitials()}
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-white">
                {getDisplayName()}
              </span>
              <span className="text-xs text-white/60">
                {t('pricing.plans.free')}
              </span>
            </div>
            <button
              onClick={signOut}
              disabled={isSigningOut}
              className="rounded p-1.5 text-white/50 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-50"
              title={t('auth.signOut')}
            >
              {isSigningOut ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LogOut className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </aside>
  )
}
